/**
 * Acta de recepción de vehículo: cómo entró el auto al taller.
 *
 * Pensada para el mostrador, con el cliente esperando: se suben todas las
 * fotos juntas y despues los datos rapidos. Queda como registro interno
 * para poder mostrarlo si aparece un reclamo por un dano que ya estaba.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import {
  ArrowLeftIcon, CameraIcon, QrCodeIcon, XCircleIcon, CheckCircleIcon,
  DocumentTextIcon, PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { getAppSocket } from '../lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';
import { OdometerScanButton } from '../components/OdometerScanner';

const NIVELES = ['Vacío', 'Reserva', '1/4', '1/2', '3/4', 'Lleno'];

const ACCESORIOS = [
  { key: 'auxilio', label: 'Rueda de auxilio' },
  { key: 'gato', label: 'Gato y llave de rueda' },
  { key: 'matafuegos', label: 'Matafuegos' },
  { key: 'balizas', label: 'Balizas' },
  { key: 'tapas', label: 'Tapas de llanta' },
  { key: 'cedula', label: 'Cédula / documentación' },
];

const VehicleReceptionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [acta, setActa] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(0);
  const [qr, setQr] = useState<{ token: string; dataUrl: string; received: number } | null>(null);
  const qrTokenRef = useRef<string | null>(null);
  const qrGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const QR_GRACE_MS = 2 * 60 * 1000;

  const endQrSession = () => {
    if (qrGraceTimerRef.current) { clearTimeout(qrGraceTimerRef.current); qrGraceTimerRef.current = null; }
    const token = qrTokenRef.current;
    qrTokenRef.current = null;
    if (token) axios.delete(`/api/receptions/photo-session/${token}`).catch(() => {});
  };
  const armQrGrace = () => {
    if (qrGraceTimerRef.current) clearTimeout(qrGraceTimerRef.current);
    qrGraceTimerRef.current = setTimeout(endQrSession, QR_GRACE_MS);
  };

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/receptions/${id}`);
      setActa(data.acta);
    } catch {
      toast.error('No se pudo cargar el acta');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Fotos que llegan del celular por QR
  useEffect(() => {
    const socket = getAppSocket();
    const onPhoto = (p: any) => {
      if (!qrTokenRef.current || p?.token !== qrTokenRef.current) return;
      setActa((prev: any) => prev ? { ...prev, photos: [...(prev.photos || []), { path: p.path, label: '' }] } : prev);
      setQr((q) => (q ? { ...q, received: q.received + 1 } : q));
      setDirty(true);
      armQrGrace();
    };
    socket.on('receptions:photo', onPhoto);
    return () => { socket.off('receptions:photo', onPhoto); };
  }, []);

  useEffect(() => () => { endQrSession(); }, []);

  const setCampo = (campo: string, valor: any) => {
    setActa((prev: any) => ({ ...prev, [campo]: valor }));
    setDirty(true);
  };

  const guardar = async (silencioso = false) => {
    if (!acta) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`/api/receptions/${acta.id}`, {
        km: acta.km, fuelLevel: acta.fuelLevel, warningLights: acta.warningLights,
        damagesNote: acta.damagesNote, accessories: acta.accessories, keysCount: acta.keysCount,
        personalItems: acta.personalItems, clientNote: acta.clientNote, photos: acta.photos
      });
      setActa(data.acta);
      setDirty(false);
      if (!silencioso) toast.success('Acta guardada');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  const subirFotos = () => fileInputRef.current?.click();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !acta) return;
    setSubiendo(files.length);
    for (const file of files) {
      const fd = new FormData();
      fd.append('image', file);
      try {
        const { data } = await axios.post(`/api/receptions/${acta.id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setActa(data.acta);
      } catch { toast.error(`No se pudo subir ${file.name}`); }
      setSubiendo((n) => n - 1);
    }
  };

  const openQr = async () => {
    try {
      const { data } = await axios.post('/api/receptions/photo-session');
      qrTokenRef.current = data.token;
      const dataUrl = await QRCode.toDataURL(data.url, { width: 240, margin: 1 });
      setQr({ token: data.token, dataUrl, received: 0 });
    } catch { toast.error('No se pudo abrir la sesión de fotos'); }
  };

  const cerrarQr = async () => {
    if (dirty) await guardar(true);
    setQr(null);
    armQrGrace();
  };

  // El PDF sale con lo guardado: si hay cambios sin guardar, van primero
  const verPdf = async () => {
    if (dirty) await guardar(true);
    try {
      const res = await axios.get(`/api/receptions/${acta.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('No se pudo generar el PDF'); }
  };

  const enviarWhatsApp = async () => {
    if (dirty) await guardar(true);
    if (!await confirmDialog({
      title: 'Enviar el acta al cliente',
      description: `Se le manda por WhatsApp el PDF con el estado del vehículo${acta.clientPhone ? ` al ${acta.clientPhone}` : ''}.`,
      confirmText: 'Enviar'
    })) return;
    setEnviando(true);
    try {
      await axios.post(`/api/receptions/${acta.id}/send-whatsapp`);
      toast.success('Acta enviada al cliente');
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo enviar');
    } finally { setEnviando(false); }
  };

  const borrarFoto = async (path: string) => {
    try {
      const { data } = await axios.delete(`/api/receptions/${acta.id}/photo`, { data: { path } });
      setActa(data.acta);
    } catch { toast.error('No se pudo borrar la foto'); }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Cargando acta...</div>;
  if (!acta) return <div className="p-8 text-center text-muted-foreground">Acta no encontrada</div>;


  return (
    <div className="mx-auto w-full max-w-3xl px-3 pb-28 pt-3 md:px-6 md:pt-6">
      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFile} />

      <div className="flex items-start gap-2">
        <Button variant="outline" size="icon" className="shrink-0" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-bold md:text-2xl">Acta {acta.receptionNumber}</h1>
          <p className="truncate text-sm text-muted-foreground">
            {[acta.clientName, acta.licensePlate, [acta.make, acta.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>

      {/* Fotos */}
      <Card className="mt-4">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base">Fotos del estado</CardTitle>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" className="hidden md:inline-flex" onClick={openQr}>
              <QrCodeIcon className="mr-1 h-4 w-4" /> Desde el celu
            </Button>
            <Button type="button" size="sm" onClick={subirFotos}>
              <CameraIcon className="mr-1 h-4 w-4" /> Subir fotos
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {(acta.photos || []).length === 0 ? (
            <button
              type="button"
              onClick={subirFotos}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-8 text-center transition-colors hover:bg-muted"
            >
              <CameraIcon className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Sacar o elegir fotos</span>
              <span className="text-xs text-muted-foreground">
                Podés seleccionar varias de una vez. Conviene los cuatro laterales, el tablero y los daños que tenga.
              </span>
            </button>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {(acta.photos || []).map((p: any, i: number) => (
                <div key={i} className="group relative">
                  <img src={p.path} alt="foto" className="aspect-square w-full rounded-md border object-cover" />
                  <button
                    type="button"
                    onClick={() => borrarFoto(p.path)}
                    className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white md:hidden md:group-hover:flex"
                  >
                    <XCircleIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={subirFotos}
                className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-border text-muted-foreground transition-colors hover:bg-muted"
              >
                <CameraIcon className="h-6 w-6" />
                <span className="text-xs">Agregar</span>
              </button>
            </div>
          )}
          {subiendo > 0 && <p className="text-xs text-muted-foreground">Subiendo {subiendo} foto(s)...</p>}
        </CardContent>
      </Card>

      {/* Estado */}
      <Card className="mt-4">
        <CardHeader className="pb-2"><CardTitle className="text-base">Estado al ingresar</CardTitle></CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Kilometraje</Label>
              <div className="flex gap-1.5">
                <Input type="number" min={0} value={acta.km ?? ''}
                  onChange={(e) => setCampo('km', e.target.value)} placeholder="Ej: 85000" />
                <OdometerScanButton onKm={(km) => setCampo('km', String(km))} className="h-10 shrink-0" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Llaves entregadas</Label>
              <Input type="number" min={0} value={acta.keysCount ?? ''} 
                onChange={(e) => setCampo('keysCount', e.target.value)} placeholder="Ej: 1" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Combustible</Label>
            <div className="flex flex-wrap gap-1.5">
              {NIVELES.map((n) => (
                <button key={n} type="button" 
                  onClick={() => setCampo('fuelLevel', n)}
                  className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                    acta.fuelLevel === n ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted'
                  }`}>{n}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Luces de tablero encendidas</Label>
            <Input value={acta.warningLights || ''} 
              onChange={(e) => setCampo('warningLights', e.target.value)} placeholder="Ej: check engine, ABS" />
          </div>
          <div className="space-y-1.5">
            <Label>Daños preexistentes</Label>
            <Textarea value={acta.damagesNote || ''} 
              onChange={(e) => setCampo('damagesNote', e.target.value)}
              placeholder="Ej: rayón en puerta trasera derecha, óptica delantera izquierda rota"
              className="min-h-[70px]" />
          </div>
        </CardContent>
      </Card>

      {/* Accesorios y observaciones */}
      <Card className="mt-4">
        <CardHeader className="pb-2"><CardTitle className="text-base">Accesorios y observaciones</CardTitle></CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {ACCESORIOS.map((a) => {
              const activo = Boolean((acta.accessories || {})[a.key]);
              return (
                <button key={a.key} type="button" 
                  onClick={() => setCampo('accessories', { ...(acta.accessories || {}), [a.key]: !activo })}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-left text-sm transition-colors ${
                    activo ? 'border-emerald-500/50 bg-emerald-500/10' : 'hover:bg-muted'
                  }`}>
                  <CheckCircleIcon className={`h-4 w-4 shrink-0 ${activo ? 'text-emerald-600' : 'text-muted-foreground/40'}`} />
                  <span className="truncate">{a.label}</span>
                </button>
              );
            })}
          </div>
          <div className="space-y-1.5">
            <Label>Objetos personales que deja</Label>
            <Input value={acta.personalItems || ''} 
              onChange={(e) => setCampo('personalItems', e.target.value)} placeholder="Ninguno / detalle" />
          </div>
          <div className="space-y-1.5">
            <Label>Lo que dice el cliente</Label>
            <Textarea value={acta.clientNote || ''} 
              onChange={(e) => setCampo('clientNote', e.target.value)}
              placeholder="Ej: hace un ruido al frenar en velocidad" className="min-h-[60px]" />
          </div>
        </CardContent>
      </Card>

      {/* Barra fija */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 px-3 py-2 backdrop-blur md:px-6">
        <div className="mx-auto flex max-w-3xl items-center gap-2">
          <span className="hidden text-xs text-muted-foreground sm:block">
            {(acta.photos || []).length} foto(s)
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={verPdf}>
              <DocumentTextIcon className="mr-1 h-4 w-4" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={enviarWhatsApp} disabled={enviando}>
              <PaperAirplaneIcon className="mr-1 h-4 w-4" /> {enviando ? 'Enviando...' : 'Enviar'}
            </Button>
            <Button size="sm" onClick={() => guardar()} disabled={saving || !dirty}>
              {saving ? 'Guardando...' : dirty ? 'Guardar' : 'Guardado'}
            </Button>
          </div>
        </div>
      </div>

      {/* QR */}
      <Dialog open={!!qr} onOpenChange={(o) => { if (!o) cerrarQr(); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Sacar fotos con el celular</DialogTitle>
            <DialogDescription>Escaneá el código y sacá las fotos desde el teléfono. Aparecen acá solas.</DialogDescription>
          </DialogHeader>
          {qr && (
            <div className="flex flex-col items-center gap-2">
              <img src={qr.dataUrl} alt="QR" className="rounded-lg border bg-white p-2" />
              <p className="text-sm text-muted-foreground">{qr.received} foto(s) recibida(s)</p>
              <Button variant="outline" className="w-full" onClick={cerrarQr}>Listo</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleReceptionPage;
